import api, { ApiResponse } from './api';
import {
  SiteContact,
  CreateSiteContactInput,
  UpdateSiteContactInput,
  BaseSignatureMethod,
  TbmSignatureMode,
  type ExternalApprovalLineDefinitionRow,
  type TbmDocumentStatus,
} from '@safetyhub/shared';
import type { BadgeTone } from '@safetyhub/ui';
import type { Locale } from './i18n/locales';
import { t as tStatic } from './i18n/translations';
import { normalizePhoneForSubmit } from './phone';

export type { SiteContact, CreateSiteContactInput };

// Types
/**
 * 문서 축 상태 — shared `TbmDocumentStatus`(서버 `ReportStatus` 어휘 그대로, `CREATING` 포함)의 web 이름.
 * 정의는 shared 하나다(web 쪽 union 복제를 두지 말 것) — 값을 늘리려면 shared를 고친다.
 *
 * 응답에도 이 타입에도 TBM 전용 alias(`CREATED`·`COMPLETED`)는 없다. 화면 라벨은 `getTBMStatusLabel`이
 * 맡는다(「생성됨」·「완료」 문구는 그대로).
 *
 * ⚠️ 구 `REVIEWING`(교육담당자 확인 중)·`IN_PROGRESS`(= 문서 `SIGNING`)는 **활동 축의 사실**이라
 * 2026-09-08 결정(`tbm-status-model-two-axes`)에서 문서 enum에서 걷어냈다. 교육이 어디까지
 * 갔는지는 이 타입이 아니라 `EducationSession.status`(+ 배포 토큰의 `firstAccessedAt`)가 답한다.
 * 세션 축의 `IN_PROGRESS`·`COMPLETED`는 이 타입에 없다 — 두 축을 섞으면 tsc가 막는다.
 */
export type TBMStatus = TbmDocumentStatus;
export type SignatureMethod = BaseSignatureMethod;

export interface EducationSession {
  id: string;
  sessionDate: string;
  createdAt: string;
  educatorName: string;
  status: 'IN_PROGRESS' | 'COMPLETED';
  completedAt: string | null;
  educatorSignerName: string | null;
  // 진행자(educator) 소속/직책 — 서버가 educatorTokenId의 SiteContact에서 합성(담당자 표시용).
  // 토큰이 비활성화(취소, 구 정책(2026-09-03 이전 1회성 완료 시) 데이터)돼도 세션 기준으로 진행자 소속/직책이 유지된다.
  educatorDepartment?: string | null;
  educatorPosition?: string | null;
  educatorSignatureData?: string | null;
  safetyCheckNotes?: string | null;
  safetyChecks?: TBMSafetyCheck[] | null;
  specialNotes?: string | null;
  attendees: Array<{ id: string; name: string; signedAt: string | null; signatureData?: string | null; signatureMethod?: string | null }>;
  educationEntries?: Array<{
    id: string;
    type: 'TEXT' | 'PHOTO';
    content?: string | null;
    file?: { id: string; url: string } | null;
    caption?: string | null;
  }>;
}

export interface TBM {
  id: string;
  companyId: string;
  siteId: string;
  inspectionId?: string;
  creatorId?: string;   // 하위 호환 (createdBy)
  authorId?: string;    // 실제 Report FK
  title: string;
  content?: TBMContent;
  // 저장용 `type` 문자열은 없다 — 모든 문서가 Composer 양식이다. 읽기는 content의 **키 존재**로
  // 고른다(`lib/tbmEducationDoc.ts`, 결정 2026-09-08).
  /** Composer 템플릿 키(예: `'tbm-meeting-log'`). 형상 판별용이 아니다. non-null — `reports.template_key` NOT NULL. */
  templateKey: string;
  status: TBMStatus;
  completedAt?: string;
  attendeeCount?: number;
  createdAt: string;
  updatedAt: string;
  site: {
    id: string;
    name: string;
    address?: string;
  };
  creator: {            // 하위 호환 (서버에서 author → creator 매핑)
    id: string;
    name: string;
    email?: string;
    deletedAt?: string | null;
  };
  author?: {            // 실제 Report relation
    id: string;
    name: string;
    deletedAt?: string | null;
  };
  // 교육 담당자 서명
  authorSignerName?: string;
  authorSignatureData?: string;
  authorSignedAt?: string;
  attendees?: TBMAttendee[];
  qrSession?: TBMQRSession;
  qrSessions?: TBMQRSession[];
  approvalSignatures?: TBMApprovalSignature[];
  approvalLineDefinition?: ExternalApprovalLineDefinitionRow[] | null;
  distributionTokens?: TBMDistributionToken[];
  educationEntries?: TBMEducationEntry[];
  shareToken?: string;
  signedCount?: number;
  _count?: {
    attendees: number;
  };
  // 다회 교육 세션
  educationStartDate?: string;
  educationEndDate?: string;
  reminderTime?: string;
  educationSessions?: EducationSession[];
  // (`tbmContentInclusion`·`tbmEvidenceContent`는 web이 읽지 않아 이 타입에 두지 않는다.)
  // 서명 모드 (Phase 2-E)
  tbmSignatureMode?: TbmSignatureMode | null;
  tbmOfflineSheetCount?: number;
  // 작성자 결재 회수 권한 (9b-server) — PENDING_APPROVAL + 작성자 + 결재 진행 전이면 canCancel=true
  canCancel?: boolean;
  cancelReason?: string;
}

export interface TBMEducationEntry {
  id: string;
  sessionId?: string | null;
  type: 'TEXT' | 'PHOTO';
  content?: string;
  caption?: string;
  url?: string;
  uploadedByName?: string;
  createdAt: string;
  annotation?: { version: number; strokes: any[] } | null;
}

export interface TBMDistributionToken {
  id: string;
  token: string;
  contactName: string;
  contactPhone: string;
  /** 진행자 소속 — 연결된 SiteContact.department에서 합성 (담당자 표시용) */
  contactDepartment?: string | null;
  /** 진행자 직책 — 연결된 SiteContact.position에서 합성 (담당자 표시용) */
  contactPosition?: string | null;
  type: 'direct' | 'delegate';
  smsStatus: string;
  createdAt: string;
  firstAccessedAt?: string | null;
  accessCount?: number;
}

export interface TBMPhotoFinding {
  description: string;
  severity?: string;
  category?: string;
  measures?: string[];
}

export interface TBMPhoto {
  photoId: string;
  photoUrl: string;
  thumbnailUrl?: string;
  caption?: string;
  source?: 'inspection' | 'upload';
  findings?: TBMPhotoFinding[];
}

export interface TBMHazardItem {
  hazard: string;               // 위험요인 설명
  measure: string;              // 주요 대책 1개 (하위호환)
  selectedMeasures?: string[];  // 선택된 대책 목록 (복수 선택)
  severity?: string;            // CRITICAL/HIGH/MEDIUM/LOW
  availableMeasures?: string[]; // 선택 가능한 대책 목록 (UI용)
  photoUrl?: string;            // 연결된 사진 URL (UI 표시용)
}

export interface TBMCriticalHazard {
  hazard: string;
  measure: string;
  selectedMeasures?: string[];  // 선택된 대책 목록 (복수 선택)
  severity?: string;            // CRITICAL/HIGH/MEDIUM/LOW
}

export interface TBMLeader {
  department?: string;          // 소속
  position?: string;            // 직책
  name: string;                 // 성명
}

export interface TBMSafetyCheck {
  hazard: string;               // 위험요인 (hazardItems에서 복사)
  /**
   * 조치 여부 — 3상태다. `undefined`=미선택(아직 아무도 확인하지 않음), `true`=예, `false`=아니오.
   *
   * boolean 2상태였을 때는 "미확인"과 "아니오"가 같은 값(false)이라, 작성 시 깔아 둔 항목이
   * 회의록에 "조치 안 함"으로 인쇄됐다 — 사실이 아닌 기록이다. 미선택은 값을 넣지 않는 것으로
   * 표현하고, 교육 완료 시 미선택이 남아 있으면 서버·프론트가 완료를 막는다.
   * ⚠️ 그래서 `!c.checked`는 "아니오"가 아니다 — '아니오'는 `c.checked === false`로 판정할 것.
   */
  checked?: boolean;
  actionRequired?: string;      // '아니오'인 경우 필요한 조치 내용
}

export interface TBMContent {
  // 기존 (하위 호환)
  workType?: string;
  hazards?: string[];
  safetyMeasures?: string[];
  specialNotes?: string;
  photos?: TBMPhoto[];
  includeAnnotations?: boolean;

  // 신규: 회의록 양식 필드
  workDescription?: string;       // 작업내용
  tbmLocation?: string;           // TBM 장소

  hazardItems?: TBMHazardItem[];  // 잠재위험요인 (상위 3개, 1:1 대책)
  reserveHazardItems?: TBMHazardItem[]; // 대기 위험요인 (삭제 시 승격용)

  criticalHazard?: TBMCriticalHazard; // 중점위험요인 (가장 심각한 1건)

  leader?: TBMLeader;            // TBM 담당자 정보

  safetyChecks?: TBMSafetyCheck[]; // 작업 전 안전조치 확인

  dailyInspectionResult?: string; // 작업 전 일일 안전점검 시행 결과

  riskAssessmentDone?: boolean;   // 위험성평가 실시여부

  tag?: '' | '긴급' | '특별' | '추가';  // TBM 태그 (경로 B에서 선택)

  [key: string]: unknown;
}

export interface TBMAttendee {
  id: string;
  tbmId: string;    // 하위 호환 (서버에서 reportId → tbmId 매핑)
  reportId?: string; // 실제 FK
  sessionId?: string | null; // 교육 세션 FK
  name: string;
  signatureData?: string;
  signatureMethod?: SignatureMethod;
  signedAt?: string;
  createdAt: string;
}

export interface TBMQRSession {
  id: string;
  tbmId: string;
  token: string;
  expiresAt: string;
  createdAt: string;
  educationSessionId?: string;
}

export interface TBMApprovalSignature {
  id: string;
  signerName: string;
  signerTitle?: string;
  approvalLevel: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  signedAt?: string;
  signatureData?: string;
  comment?: string;
  round: number;
}

// DTOs
// 생성·수정 DTO를 두지 않는다 — 신규 TBM은 Composer 양식(`tbm-meeting-log`)이 만들고, 제목·본문
// 수정은 Composer 편집(`editReport`)이, 교육 기록은 세션 축(`updateTbmSessionRecords*`)이 맡는다.

// API Functions
// (TBM 목록 클라이언트를 두지 않는다 — 문서 목록은 `getReports`(`lib/reports.ts`) 하나다.)

export async function getTBM(id: string, companyId: string): Promise<TBM> {
  const response = await api.get<ApiResponse<TBM>>(
    `/reports/tbm/${id}?companyId=${companyId}`
  );
  return response.data.data!;
}


export async function distributeTBM(
  id: string,
  companyId: string
): Promise<TBMQRSession> {
  const response = await api.post<ApiResponse<TBMQRSession>>(
    `/reports/tbm/${id}/distribute?companyId=${companyId}`,
    {}
  );
  return response.data.data!;
}

/**
 * 교육 회차 완료 (인증 경로).
 *
 * `sessionId`(F-21): **화면이 닫으려는 회차**를 서버에 말한다. 종전에는 대상을 말하지 않아
 * 서버가 활성 회차를 스스로 찾았고, 그 사이 다른 탭·담당자 링크가 그 회차를 닫고 다음 회차를
 * 열면 사용자가 **보던 회차가 아닌 회차**를 자기 서명으로 닫았다(활성 회차가 없으면 빈 회차가
 * 새로 생겼다). 불일치면 서버가 409 `TBM_EDUCATION_SESSION_MISMATCH`를 준다.
 * 대상을 특정할 수 없는 화면(구 TBM content 축 등)은 넘기지 않는다 — 그때는 종전 동작.
 */
export async function completeTBM(
  id: string,
  companyId: string,
  signature?: { name: string; signatureData: string },
  safetyCheckNotes?: string,
  safetyChecks?: TBMSafetyCheck[],
  specialNotes?: string,
  sessionId?: string,
): Promise<TBM> {
  const response = await api.post<ApiResponse<TBM>>(
    `/reports/tbm/${id}/complete?companyId=${companyId}`,
    {
      ...signature,
      ...(safetyCheckNotes ? { safetyCheckNotes } : {}),
      ...(safetyChecks ? { safetyChecks } : {}),
      ...(specialNotes !== undefined ? { specialNotes } : {}),
      ...(sessionId ? { sessionId } : {}),
    },
  );
  return response.data.data!;
}

/**
 * 교육 세션 기록 갱신 (안전조치 확인 / 시행 결과 / 특이사항).
 * 미확정(APPROVED/PENDING_APPROVAL/CANCELLED 아님) TBM의 세션 기록을 갱신.
 * 완료된(COMPLETED) 세션도 수정 가능. 갱신된 TBM 반환.
 */
export async function updateTbmSessionRecords(
  id: string,
  sessionId: string,
  companyId: string,
  records: { safetyChecks?: TBMSafetyCheck[]; safetyCheckNotes?: string; specialNotes?: string },
): Promise<TBM> {
  const response = await api.patch<ApiResponse<TBM>>(
    `/reports/tbm/${id}/sessions/${sessionId}/records?companyId=${companyId}`,
    records,
  );
  return response.data.data!;
}

// (TBM 전용 제출·취소·삭제·재전송 클라이언트는 없다 — Composer 상세의 결재 액션과
//  `lib/reports.ts`·`lib/tbmEducatorResend.ts`가 담당한다.)

// Public API (no auth required)
export async function getTBMByToken(token: string, via?: string | null): Promise<TBM> {
  const url = via
    ? `/reports/tbm/sign/${token}?via=${encodeURIComponent(via)}`
    : `/reports/tbm/sign/${token}`;
  const response = await api.get<ApiResponse<TBM>>(url);
  return response.data.data!;
}

export async function signByToken(
  token: string,
  name: string,
  data: { signatureData?: string; signatureMethod: SignatureMethod; signatureSource?: 'worker_self' | 'manager_proxy' | 'educator_proxy'; viewedLanguage?: string },
  accessMethod?: 'QR_SCAN' | 'TABLET',
): Promise<TBMAttendee> {
  const response = await api.post<ApiResponse<TBMAttendee>>(`/reports/tbm/sign/${token}`, {
    name,
    ...data,
    ...(accessMethod && { accessMethod }),
  });
  return response.data.data!;
}

// (TBM 전용 API 클라이언트를 두지 않는다 — 공종 그룹핑은 `report-split` 분할 계획이, 회차 목록은
//  `getTBM` 응답의 `educationSessions`가, PDF는 공용 `lib/reports.ts`의 `generatePdf`가 맡는다.)

// ============================================
// SiteContact (현장 담당자) API
// Types: SiteContact, CreateSiteContactInput, UpdateSiteContactInput
// → @safetyhub/shared에서 import (파일 상단 참조)
// ============================================

export async function getSiteContacts(
  companyId: string,
  siteId: string,
): Promise<SiteContact[]> {
  const response = await api.get<ApiResponse<SiteContact[]>>(
    `/organizations/${companyId}/sites/${siteId}/contacts`,
  );
  return response.data.data || [];
}

export async function createSiteContact(
  companyId: string,
  siteId: string,
  data: CreateSiteContactInput,
): Promise<SiteContact> {
  const response = await api.post<ApiResponse<SiteContact>>(
    `/organizations/${companyId}/sites/${siteId}/contacts`,
    normalizeContactPhone(data),
  );
  return response.data.data!;
}

/**
 * 전송 경계 방어 — 입력란이 이미 숫자만 유지하지만(lib/phone), 다른 경로에서 들어온 값도
 * 서버 계약(`^01[016789]\d{7,8}$`)에 맞춰 보낸다. 규칙 정본은 lib/phone 하나다.
 */
function normalizeContactPhone<T extends { phone?: string }>(data: T): T {
  const phone = normalizePhoneForSubmit(data.phone);
  return phone === data.phone ? data : { ...data, phone: phone as T['phone'] };
}

export async function updateSiteContact(
  companyId: string,
  siteId: string,
  contactId: string,
  data: UpdateSiteContactInput,
): Promise<SiteContact> {
  const response = await api.patch<ApiResponse<SiteContact>>(
    `/organizations/${companyId}/sites/${siteId}/contacts/${contactId}`,
    data,
  );
  return response.data.data!;
}

export async function deleteSiteContact(
  companyId: string,
  siteId: string,
  contactId: string,
): Promise<void> {
  await api.delete(
    `/organizations/${companyId}/sites/${siteId}/contacts/${contactId}`,
  );
}

// (담당자 추천 클라이언트는 없다 — 서버에도 그 엔드포인트가 없다.)

// (TBM 배포·위임 클라이언트는 없다 — 배포는 `tbm-distribution-setup`(흐름 레지스트리), 위임은
//  `createDelegatedComposedReport`(`lib/reports.ts`)가 담당한다.)

// ============================================
// Educator Token (담당자 전용) Types & API
// ============================================

export interface EducatorTokenInfo {
  id: string;
  reportId?: string;
  inspectionId?: string;
  companyId: string;
  siteId: string;
  contactName: string;
  contactPhone: string;
  contactDepartment?: string | null;
  contactPosition?: string | null;
  /** 교육 진행 토큰뿐이다 — `'delegate'`(위임 작성) 토큰을 발급하는 경로가 없다. */
  type: 'direct';
  token: string;
  expiresAt: string;
  isActive: boolean;
}

/**
 * `GET /reports/tbm/educator/:token` 응답 — 교육 진행 토큰 + 문서. delegate 변형도 그것을 가르는
 * discriminator(`type`·`phase`)도 없다 — 토큰 종류는 `token.type` 컬럼이 그대로 싣는다.
 */
export interface EducatorTokenResult {
  token: EducatorTokenInfo;
  tbm: TBM;
}

/** 담당자 토큰으로 TBM/점검 조회 */
export async function getByEducatorToken(token: string, via?: string | null): Promise<EducatorTokenResult> {
  const url = via
    ? `/reports/tbm/educator/${token}?via=${encodeURIComponent(via)}`
    : `/reports/tbm/educator/${token}`;
  const response = await api.get<ApiResponse<EducatorTokenResult>>(url);
  return response.data.data!;
}

/** 담당자 교육 시작 (QR 생성) */
export async function startEducationByToken(token: string): Promise<TBMQRSession> {
  const response = await api.post<ApiResponse<TBMQRSession>>(
    `/reports/tbm/educator/${token}/start`,
  );
  return response.data.data!;
}

/** 담당자 최종 서명 + 완료 */
export async function completeByEducatorToken(
  token: string,
  // sessionId(F-21): 닫으려는 회차 — 뜻과 판정은 completeTBM의 같은 인자와 같다.
  signature: { signatureData?: string; signatureMethod: string; safetyCheckNotes?: string; safetyChecks?: TBMSafetyCheck[]; specialNotes?: string; sessionId?: string },
): Promise<TBM> {
  const response = await api.post<ApiResponse<TBM>>(
    `/reports/tbm/educator/${token}/complete`,
    signature,
  );
  return response.data.data!;
}

/**
 * 담당자 토큰으로 교육 세션 기록 갱신 (안전조치 확인 / 시행 결과 / 특이사항).
 * 미확정 TBM의 세션 기록을 자동저장. 갱신된 TBM 반환.
 */
export async function updateTbmSessionRecordsByToken(
  token: string,
  sessionId: string,
  records: { safetyChecks?: TBMSafetyCheck[]; safetyCheckNotes?: string; specialNotes?: string },
): Promise<TBM> {
  const response = await api.patch<ApiResponse<TBM>>(
    `/reports/tbm/educator/${token}/sessions/${sessionId}/records`,
    records,
  );
  return response.data.data!;
}

/**
 * 토큰 교육담당자가 교육 시작 전 입력 시 세션을 lazy 생성하고 기록을 저장한다.
 * 활성 세션이 없으면 서버가 세션을 생성한 뒤 기록을 저장하고 educationSessionId를 반환한다.
 */
export async function upsertTbmSessionRecordsByToken(
  token: string,
  records: { safetyChecks?: TBMSafetyCheck[]; safetyCheckNotes?: string; specialNotes?: string },
): Promise<{ educationSessionId: string }> {
  const response = await api.patch<ApiResponse<{ educationSessionId: string }>>(
    `/reports/tbm/educator/${token}/records`,
    records,
  );
  return response.data.data!;
}

// (delegate 토큰 클라이언트는 없다 — 위임 작성은 `createDelegatedComposedReport`(`lib/reports.ts`)와
//  `/external/composed/:token`이, 담당자 등록은 그 위임 경로가 맡는다. 서버에도 그 엔드포인트가 없다.)

// ============================================
// 교육 현장 기록 API
// ============================================

/** 안전관리자 - 텍스트 기록 추가 */
export async function addEducationText(
  tbmId: string,
  content: string,
  companyId: string,
): Promise<TBMEducationEntry> {
  const response = await api.post<ApiResponse<TBMEducationEntry>>(
    `/reports/tbm/${tbmId}/education-entries?companyId=${companyId}`,
    { type: 'TEXT', content },
  );
  return response.data.data!;
}

/** 안전관리자 - 사진 기록 추가 */
export async function uploadEducationPhoto(
  tbmId: string,
  file: File,
  companyId: string,
  caption?: string,
): Promise<TBMEducationEntry> {
  const formData = new FormData();
  formData.append('file', file);
  if (caption) formData.append('caption', caption);
  const response = await api.post<ApiResponse<TBMEducationEntry>>(
    `/reports/tbm/${tbmId}/education-entries?companyId=${companyId}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data.data!;
}

/** 안전관리자 - 기록 삭제 */
export async function deleteEducationEntry(
  tbmId: string,
  entryId: string,
  companyId: string,
): Promise<void> {
  await api.delete(
    `/reports/tbm/${tbmId}/education-entries/${entryId}?companyId=${companyId}`,
  );
}

/** 교육담당자 - 텍스트 기록 추가 */
export async function addEducationTextByToken(
  token: string,
  content: string,
): Promise<TBMEducationEntry> {
  const response = await api.post<ApiResponse<TBMEducationEntry>>(
    `/reports/tbm/educator/${token}/education-entries`,
    { type: 'TEXT', content },
  );
  return response.data.data!;
}

/** 교육담당자 - 사진 기록 추가 */
export async function uploadEducationPhotoByToken(
  token: string,
  file: File,
  caption?: string,
): Promise<TBMEducationEntry> {
  const formData = new FormData();
  formData.append('file', file);
  if (caption) formData.append('caption', caption);
  const response = await api.post<ApiResponse<TBMEducationEntry>>(
    `/reports/tbm/educator/${token}/education-entries`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data.data!;
}

/** 안전관리자 - 텍스트 기록 수정 */
export async function updateEducationText(
  tbmId: string,
  entryId: string,
  content: string,
  companyId: string,
): Promise<TBMEducationEntry> {
  const response = await api.patch<ApiResponse<TBMEducationEntry>>(
    `/reports/tbm/${tbmId}/education-entries/${entryId}?companyId=${companyId}`,
    { content },
  );
  return response.data.data!;
}

/** 교육담당자 - 텍스트 기록 수정 */
export async function updateEducationTextByToken(
  token: string,
  entryId: string,
  content: string,
): Promise<TBMEducationEntry> {
  const response = await api.patch<ApiResponse<TBMEducationEntry>>(
    `/reports/tbm/educator/${token}/education-entries/${entryId}`,
    { content },
  );
  return response.data.data!;
}

/** 교육담당자 - 기록 삭제 */
export async function deleteEducationEntryByToken(
  token: string,
  entryId: string,
): Promise<void> {
  await api.delete(
    `/reports/tbm/educator/${token}/education-entries/${entryId}`,
  );
}

// ============================================
// 어노테이션 API
// ============================================

/** 안전관리자 - 어노테이션 조회 */
export async function getEducationAnnotation(
  tbmId: string,
  entryId: string,
  companyId: string,
): Promise<{ data: { version: number; strokes: any[] } } | null> {
  const response = await api.get<ApiResponse<{ data: any } | null>>(
    `/reports/tbm/${tbmId}/education-entries/${entryId}/annotation?companyId=${companyId}`,
  );
  return response.data.data || null;
}

/** 안전관리자 - 어노테이션 저장 */
export async function saveEducationAnnotation(
  tbmId: string,
  entryId: string,
  companyId: string,
  data: { version: number; strokes: any[] },
): Promise<any> {
  const response = await api.patch<ApiResponse<any>>(
    `/reports/tbm/${tbmId}/education-entries/${entryId}/annotation?companyId=${companyId}`,
    { data },
  );
  return response.data.data;
}

/** 안전관리자 - 교육 기록 순서 변경 */
export async function reorderEducationEntries(
  tbmId: string,
  entries: Array<{ id: string; displayOrder: number }>,
  companyId: string,
): Promise<void> {
  await api.patch(
    `/reports/tbm/${tbmId}/education-entries/reorder?companyId=${companyId}`,
    { entries },
  );
}

/** 교육담당자 - 어노테이션 조회 */
export async function getEducationAnnotationByToken(
  token: string,
  entryId: string,
): Promise<{ data: { version: number; strokes: any[] } } | null> {
  const response = await api.get<ApiResponse<{ data: any } | null>>(
    `/reports/tbm/educator/${token}/education-entries/${entryId}/annotation`,
  );
  return response.data.data || null;
}

/** 교육담당자 - 어노테이션 저장 */
export async function saveEducationAnnotationByToken(
  token: string,
  entryId: string,
  data: { version: number; strokes: any[] },
): Promise<any> {
  const response = await api.patch<ApiResponse<any>>(
    `/reports/tbm/educator/${token}/education-entries/${entryId}/annotation`,
    { data },
  );
  return response.data.data;
}

/** 교육담당자 - 교육 기록 순서 변경 */
export async function reorderEducationEntriesByToken(
  token: string,
  entries: Array<{ id: string; displayOrder: number }>,
): Promise<void> {
  await api.patch(
    `/reports/tbm/educator/${token}/education-entries/reorder`,
    { entries },
  );
}

// AI 분석 (TBM 경로 B용)
export interface TBMPhotoAnalysisResult {
  findings: Array<{
    description: string;
    severity: string;
    measures: string[];
    category?: string;
  }>;
}

export async function analyzePhotoForTBM(
  photoUrl: string,
  companyId: string,
): Promise<TBMPhotoAnalysisResult> {
  const response = await api.post<ApiResponse<TBMPhotoAnalysisResult>>(
    '/reports/tbm/analyze-photo',
    { photoUrl, companyId },
  );
  return response.data.data!;
}

// Helpers
/** TBM 상태 → 화면 라벨. 문구는 alias 시절 그대로(DRAFT=「생성됨」, APPROVED=「완료」) — 교육 화면의 어휘다. */
export function getTBMStatusLabel(status: TBMStatus, locale: Locale = 'ko'): string {
  const keyMap: Record<TBMStatus, Parameters<typeof tStatic>[1]> = {
    CREATING: 'badge.status.creating',
    DRAFT: 'badge.status.created',
    PENDING_APPROVAL: 'badge.status.pendingApproval',
    APPROVED: 'badge.status.completed',
    REJECTED: 'badge.status.rejected',
    CANCELLED: 'badge.status.cancelled',
  };
  return tStatic(locale, keyMap[status]) || status;
}

/** TBM 상태 → 공용 Badge tone. 색 축의 단일 정본이다. */
export function getTBMStatusBadgeTone(status: TBMStatus): BadgeTone {
  const tones: Record<TBMStatus, BadgeTone> = {
    CREATING: 'amber',
    DRAFT: 'slate',
    PENDING_APPROVAL: 'amber',
    APPROVED: 'emerald',
    REJECTED: 'rose',
    CANCELLED: 'rose',
  };
  return tones[status] || 'slate';
}


/** TBM 태그 뱃지 라벨 + tone 반환 (태그 없으면 null). 태그 어휘(긴급·특별·추가)는 아래 case가 정본이다. */
export function getTBMTagStyle(tag?: string, locale: Locale = 'ko'): { label: string; tone: BadgeTone } | null {
  switch (tag) {
    case '긴급': return { label: tStatic(locale, 'badge.tag.urgent'), tone: 'rose' };
    case '특별': return { label: tStatic(locale, 'badge.tag.special'), tone: 'violet' };
    case '추가': return { label: tStatic(locale, 'badge.tag.extra'), tone: 'blue' };
    default: return null;
  }
}

// (상태 역매핑을 두지 않는다 — `TBMStatus`는 `ReportStatus` 어휘 그대로이고, 문서관리 목록도
//  그 어휘를 그대로 쓴다.)

// TBM 콘텐츠 유틸리티
export function buildCriticalHazardMeasureSet(criticalHazard: TBMCriticalHazard | undefined | null): Set<string> {
  return new Set(
    criticalHazard?.selectedMeasures?.length
      ? criticalHazard.selectedMeasures
      : criticalHazard?.measure
        ? [criticalHazard.measure]
        : [],
  );
}

// (공유/외부결재 응답을 양식용 형상으로 변환하는 매퍼를 두지 않는다 — `ComposedDocumentBody`가
//  응답을 그대로 그린다.)

export function getExtraMeasures(chMeasureSet: Set<string>, itemMeasures: string[]): string[] {
  const itemSet = new Set(itemMeasures);
  return Array.from(chMeasureSet).filter((m) => !itemSet.has(m));
}
